import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import useTurnstile from "../lib/useTurnstile";
import { useAuth } from "../context/AuthContext";
import { Sparkles, ShoppingBag, Store, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

// /demo — 1-click into a curated demo. The tiny legal footer + the
// /legal/demo-nda page are the visible IP-protection posture; we don't
// pop a confidentiality modal on signup anymore (see iteration 43).
//
// Anyone can hit this URL. No login required. No real money.
// Bot protection: when REACT_APP_TURNSTILE_SITE_KEY is set, every demo
// launch attempts an invisible Cloudflare Turnstile challenge. Backend
// also rate-limits /api/demo/start to 10/hour per IP regardless.
export default function DemoLanding() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null); // role currently launching
  const { mount: tsMount, getToken, configured: tsConfigured } = useTurnstile();

  const start = async (role) => {
    setLoading(role);
    try {
      sessionStorage.setItem("app_mode_override", "demo");
      // Bot gate — only when Turnstile is configured. null = challenge failed.
      const token = await getToken();
      if (tsConfigured && token === null) {
        toast.error("Please complete the human verification check below before continuing.");
        // Scroll the widget into view so the user can see it.
        try { document.querySelector('[data-testid="cf-turnstile-host"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
        setLoading(null);
        return;
      }
      const headers = token ? { "cf-turnstile-response": token } : {};
      const { data } = await api.post(
        "/demo/start",
        { role, turnstile_token: token || null },
        { headers },
      );
      if (data.token) localStorage.setItem("token", data.token);
      setUser(data);
      nav(`/${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start demo — try again");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-foreground text-background grid lg:grid-cols-[1.05fr_1fr] relative overflow-hidden">
      {/* LEFT — pitch */}
      <div className="p-10 lg:p-16 flex flex-col justify-center" data-testid="demo-landing">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-background flex items-center justify-center">
            <span className="text-foreground font-bold display">v</span>
          </div>
          <span className="display font-semibold text-lg">verqo</span>
          <span className="ml-2 chip text-[10px] mono uppercase tracking-widest bg-accent text-foreground">DEMO</span>
        </div>

        <h1 className="display text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] mt-12 max-w-xl">
          The B2B wholesale OS,<br/>in <span className="text-accent">60 seconds</span>.
        </h1>
        <p className="mt-5 text-background/65 max-w-md text-[15px] leading-relaxed">
          Pick a role below and you'll land in a fully populated portal — orders, invoices,
          a $50K pre-approved credit line, AI underwriting memo, real shipping rates.
          No signup, no real money, full feature unlock.
        </p>

        <div className="grid gap-3 mt-12 max-w-lg">
          <RoleCard role="buyer" Icon={ShoppingBag} title="I'm a Buyer" subtitle="See the buyer experience — pre-approved Net-30, Plaid bank connect, AI credit memo, smart shipping" loading={loading} onClick={() => start("buyer")}/>
          <RoleCard role="seller" Icon={Store} title="I'm a Seller" subtitle="Manage your storefront — orders, payouts, multi-carrier shipping, buyer credit visibility" loading={loading} onClick={() => start("seller")}/>
        </div>

        <div className="mt-10 text-[11px] mono uppercase tracking-widest text-background/40">
          Real platform · Real integrations · Sandboxed for safety
        </div>

        {/* Cloudflare Turnstile widget host. `interaction-only` mode keeps
            it visually hidden unless CF needs the visitor to interact. */}
        <div ref={tsMount} className="mt-3" data-testid="cf-turnstile-host" />

        {/* Tiny legal footer — confidentiality posture in one line */}
        <div className="mt-6 max-w-lg text-[10px] text-background/40 leading-relaxed" data-testid="demo-legal-footer">
          Confidential evaluation — do not redistribute, screenshot, reverse-engineer, or train AI models.{" "}
          <Link to="/legal/demo-nda" target="_blank" rel="noopener noreferrer" className="underline hover:text-background/70" data-testid="demo-nda-fullpage-link">NDA</Link>
          {" · "}<Link to="/legal/acceptable-use" target="_blank" rel="noopener noreferrer" className="underline hover:text-background/70">AUP</Link>
          {" · "}<Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-background/70">Privacy</Link>
          {" · "}<Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-background/70">Terms</Link>
          {" "}· © 2026 verqo
        </div>
      </div>

      {/* RIGHT — visual */}
      <div className="hidden lg:flex items-center justify-center bg-background text-foreground p-12 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-accent/20 via-transparent to-transparent"/>
        <div className="relative max-w-md space-y-5">
          <Stat l="Pre-approved credit" v="$50,000" sub="AI-underwritten · 94% confidence"/>
          <Stat l="Active orders" v="8" sub="3 in-transit · 5 delivered"/>
          <Stat l="Avg monthly revenue" v="$480K" sub="Plaid-verified · 24mo history"/>
          <Stat l="Active integrations" v="9" sub="Plaid · Middesk · Verqo Freight · MT · Verqo AI"/>
          <div className="rounded-2xl border border-border bg-secondary/40 p-5 mt-8">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent"/>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">AI memo · live</div>
            </div>
            <p className="text-[13px] mt-2 leading-relaxed">
              "Demo Wholesale Co. is a 4-year-old California LLC in good SOS standing, no liens, $480K avg
              monthly revenue, 0 NSFs in 24 months. <strong>Recommend $50K Net-30, 94% confidence.</strong>"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ role, Icon, title, subtitle, onClick, loading }) {
  const isLoading = loading === role;
  const isDisabled = !!loading;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      data-testid={`demo-start-${role}`}
      className={`group text-left rounded-2xl border border-background/15 bg-background/5 hover:bg-background/10 backdrop-blur-sm p-5 flex items-center gap-4 transition-all ${isDisabled ? "opacity-60 cursor-wait" : "hover:border-accent/50 hover:translate-y-[-1px]"}`}
    >
      <div className="w-11 h-11 rounded-xl bg-background/10 grid place-items-center shrink-0 group-hover:bg-accent/20 transition-colors">
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Icon className="w-5 h-5"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="display text-base">{title}</div>
        <div className="text-[12.5px] text-background/65 mt-0.5 leading-snug">{subtitle}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-background/40 group-hover:text-accent group-hover:translate-x-0.5 transition-all"/>
    </button>
  );
}

function Stat({ l, v, sub }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">{l}</div>
      <div className="display text-3xl mt-1">{v}</div>
      <div className="text-[11px] text-foreground/55 mt-0.5">{sub}</div>
    </div>
  );
}
